import { nowIsoUtc } from "@nijiurachan/js/util/time";
import { waitForBrowserAuthorization } from "@/features/app-auth/api/appAuth";
import { apiDelete, apiGet } from "@/shared/api/client";
import {
  createMediator,
  type Mediator,
  type MediatorTransitionResult,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export interface ConnectedApplication {
  id: string;
  clientName: string;
  issuedAt: string;
  expiresAt: string;
  revokedAt: string | null;
}

export interface ConnectedApplicationsState {
  applications: ConnectedApplication[] | null;
  phase: "idle" | "loading" | "revoking";
  message: string;
}

export type ConnectedApplicationsEvent =
  | { type: "connected-applications.load" }
  | { type: "connected-applications.revoke"; id: string }
  | {
      type: "connected-applications.loaded";
      applications: ConnectedApplication[];
    }
  | { type: "connected-applications.revoked"; id: string; revokedAt: string }
  | { type: "connected-applications.failed"; message: string };

type Effect = { type: "load" } | { type: "revoke"; id: string };

export function isConnectedApplicationsEvent(
  event: TypedEvent,
): event is ConnectedApplicationsEvent {
  return [
    "connected-applications.load",
    "connected-applications.revoke",
    "connected-applications.loaded",
    "connected-applications.revoked",
    "connected-applications.failed",
  ].includes(event.type);
}

export function transitionConnectedApplications(
  state: ConnectedApplicationsState,
  event: ConnectedApplicationsEvent,
): MediatorTransitionResult<ConnectedApplicationsState, Effect> {
  switch (event.type) {
    case "connected-applications.load":
      return state.phase !== "idle"
        ? { status: "handled", state }
        : {
            status: "handled",
            state: {
              ...state,
              phase: "loading",
              message: "ブラウザ認証と接続済みアプリを確認しています…",
            },
            effects: [{ type: "load" }],
          };
    case "connected-applications.revoke":
      if (
        state.phase !== "idle" ||
        !state.applications?.some(
          (application) =>
            application.id === event.id && application.revokedAt === null,
        )
      )
        return { status: "handled", state };
      return {
        status: "handled",
        state: { ...state, phase: "revoking" },
        effects: [{ type: "revoke", id: event.id }],
      };
    case "connected-applications.loaded":
      return {
        status: "handled",
        state: {
          phase: "idle",
          applications: event.applications,
          message:
            event.applications.length === 0
              ? "接続済みアプリはありません。"
              : "取り消すアプリを選択してください。",
        },
      };
    case "connected-applications.revoked":
      return {
        status: "handled",
        state: {
          phase: "idle",
          applications:
            state.applications?.map((row) =>
              row.id === event.id
                ? { ...row, revokedAt: event.revokedAt }
                : row,
            ) ?? null,
          message: "アプリの利用許可を取り消しました。",
        },
      };
    case "connected-applications.failed":
      return {
        status: "handled",
        state: { ...state, phase: "idle", message: event.message },
      };
  }
}

export interface ConnectedApplicationsServices {
  authenticate: (signal: AbortSignal) => Promise<string>;
  list: (token: string, signal: AbortSignal) => Promise<ConnectedApplication[]>;
  revoke: (id: string, token: string, signal: AbortSignal) => Promise<unknown>;
}

const defaultServices: ConnectedApplicationsServices = {
  authenticate: waitForBrowserAuthorization,
  list: (token: string, signal: AbortSignal) =>
    apiGet<ConnectedApplication[]>("/app-credentials", {
      token,
      credentials: "include",
      cache: "no-store",
      signal,
    }),
  revoke: (id: string, token: string, signal: AbortSignal) =>
    apiDelete(
      `/app-credentials/${id}`,
      {},
      { token, credentials: "include", signal },
    ),
};

export function createConnectedApplicationsMediator(
  services: ConnectedApplicationsServices = defaultServices,
): Mediator<ConnectedApplicationsState, ConnectedApplicationsEvent, Effect> {
  let active: AbortController | null = null;
  let token: string | undefined;
  const mediator = createMediator<
    ConnectedApplicationsState,
    ConnectedApplicationsEvent,
    Effect
  >(
    { applications: null, phase: "idle", message: "" },
    transitionConnectedApplications,
    {
      runEffect(effect: Effect): void {
        active?.abort();
        const request = new AbortController();
        active = request;
        void (async (): Promise<void> => {
          try {
            if (effect.type === "load") {
              const browserToken = await services.authenticate(request.signal);
              if (request.signal.aborted) return;
              const applications = await services.list(
                browserToken,
                request.signal,
              );
              if (request.signal.aborted) return;
              token = browserToken;
              mediator.dispatch({
                type: "connected-applications.loaded",
                applications,
              });
            } else {
              if (!token) throw new Error("一覧を取得できませんでした。");
              await services.revoke(effect.id, token, request.signal);
              if (request.signal.aborted) return;
              mediator.dispatch({
                type: "connected-applications.revoked",
                id: effect.id,
                revokedAt: nowIsoUtc(),
              });
            }
          } catch (error) {
            if (!request.signal.aborted)
              mediator.dispatch({
                type: "connected-applications.failed",
                message:
                  error instanceof Error
                    ? error.message
                    : effect.type === "load"
                      ? "一覧を取得できませんでした。"
                      : "取り消しに失敗しました。",
              });
          }
        })();
      },
    },
  );
  return {
    ...mediator,
    get isDisposed(): boolean {
      return mediator.isDisposed;
    },
    dispose(): void {
      active?.abort();
      token = undefined;
      mediator.dispose();
    },
  };
}
